import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, UserCheck } from "lucide-react";
import { MaskReveal, Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { BookingForm } from "../components/BookingForm";
import { WhatsAppButton, CallButton } from "../components/WhatsAppButton";
import { Seo } from "../components/Seo";
import { SERVICES, SERVICES_PATH } from "../data/siteData";

export default function ServiceDetail() {
  const { slug } = useParams();
  const index = SERVICES.findIndex((s) => s.slug === slug);
  const service = SERVICES[index];
  if (!service) return <Navigate to={SERVICES_PATH} replace />;

  const prev = SERVICES[(index - 1 + SERVICES.length) % SERVICES.length];
  const next = SERVICES[(index + 1) % SERVICES.length];

  return (
    <>
      <Seo
        title={`${service.name} in Suchitra, Hyderabad | Belong Salon & Spa`}
        description={`${service.short} ${service.durations.join(" / ")} sessions at Belong Salon & Spa, Suchitra, Hyderabad. Book on WhatsApp or call 098666 34473.`}
        keywords={`${service.name} Hyderabad, ${service.name} Suchitra, ${service.tagline}, spa in Quthbullapur`}
      />
      <section className="relative overflow-hidden pb-24 pt-40" data-testid="service-detail-hero">
        <div className="ambient-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
          <div>
            <MaskReveal delay={0.1}>
              <Link
                to={SERVICES_PATH}
                data-testid="service-back-link"
                className="inline-flex items-center gap-2 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-stone-500 transition-colors hover:text-amber-200"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> All Services
              </Link>
            </MaskReveal>
            <MaskReveal delay={0.2}>
              <p className="mt-8 font-mono-alt text-[11px] uppercase tracking-[0.35em] text-amber-400/80">
                Service {String(index + 1).padStart(2, "0")} — {service.tagline}
              </p>
            </MaskReveal>
            <h1 className="mt-6 font-display text-4xl font-light leading-[1.06] tracking-tight text-amber-50 sm:text-6xl">
              <MaskReveal delay={0.35}>{service.name}</MaskReveal>
            </h1>
            <MaskReveal delay={0.55} className="mt-8">
              <p className="max-w-lg text-base font-light leading-relaxed text-stone-400">{service.short}</p>
            </MaskReveal>

            <Reveal delay={0.7} className="mt-10">
              <p className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-amber-500/70">
                Session Lengths {service.minNote ? `· ${service.minNote}` : ""}
              </p>
              <div className="mt-4 flex flex-wrap gap-3" data-testid="service-durations">
                {service.durations.map((d) => (
                  <span
                    key={d}
                    data-testid={`duration-chip-${d.replace(" ", "-")}`}
                    className="rounded-full border border-amber-500/25 bg-amber-400/5 px-5 py-2 font-mono-alt text-[11px] uppercase tracking-[0.18em] text-amber-200"
                  >
                    {d}
                  </span>
                ))}
              </div>
              <p className="mt-6 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-stone-500" data-testid="service-price-note">
                Price on request — confirmed on call or WhatsApp
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <CallButton label="Call to Book" testid="service-call-to-book-btn" />
                <WhatsAppButton
                  message={`Hello Belong Spa, I would like to book a ${service.name} session.`}
                  label="WhatsApp Booking"
                  testid="service-whatsapp-booking-btn"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.3} className="relative">
            <div className="spotlight-frame overflow-hidden rounded-t-full border border-amber-500/25">
              <img src={service.image} alt={service.name} className="aspect-[3/4] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/60 via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-amber-500/10 bg-[#0D0D0F] py-24" data-testid="service-what-it-is">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-400/80">What It Is</p>
          </Reveal>
          <div className="mt-8 grid gap-14 lg:grid-cols-2">
            <Reveal delay={0.1}>
              <p className="font-display text-2xl font-light leading-relaxed text-stone-200 sm:text-3xl">
                {service.long}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-400/80">Who It Is For</p>
              <ul className="mt-6 space-y-5" data-testid="service-audience-list">
                {service.audience.map((a, i) => (
                  <li key={a} className="flex items-start gap-4" data-testid={`audience-item-${i}`}>
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-amber-500/25 bg-amber-400/5">
                      <UserCheck className="h-3.5 w-3.5 text-amber-300" />
                    </span>
                    <span className="text-base font-light leading-relaxed text-stone-300">{a}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="service-benefits">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-400/80">Benefits</p>
            <h2 className="mt-5 font-display text-3xl font-light tracking-tight text-amber-50 sm:text-4xl">
              What this service gives back
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {service.benefits.map((b, i) => (
              <Reveal key={b} delay={i * 0.07}>
                <div
                  className="flex items-start gap-4 rounded-2xl border border-white/8 bg-[#121215] p-6"
                  data-testid={`benefit-item-${i}`}
                >
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-400/15">
                    <Check className="h-3.5 w-3.5 text-amber-300" />
                  </span>
                  <p className="text-sm font-light leading-relaxed text-stone-300">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-amber-500/10 bg-[#0D0D0F] py-24" data-testid="service-booking-section">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <p className="font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-400/80">Book This Service</p>
            <h2 className="mt-5 font-display text-3xl font-light leading-[1.12] tracking-tight text-amber-50 sm:text-4xl">
              Pick a duration, <em className="text-gold-gradient not-italic">claim a slot.</em>
            </h2>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-stone-400">
              Choose your duration and preferred time below — your selection is sent straight to our
              WhatsApp, and we confirm your suite within minutes.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <BookingForm defaultService={service.slug} />
          </Reveal>
        </div>
      </section>

      <section className="py-16" data-testid="service-prev-next">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 lg:px-10">
          <Link
            to={`${SERVICES_PATH}/${prev.slug}`}
            data-testid="service-prev-link"
            className="group flex items-center gap-3 text-left"
          >
            <ArrowLeft className="h-4 w-4 text-stone-600 transition-colors group-hover:text-amber-300" />
            <span>
              <span className="block font-mono-alt text-[9px] uppercase tracking-[0.25em] text-stone-600">Previous</span>
              <span className="font-display text-lg font-light text-stone-300 transition-colors group-hover:text-amber-200 sm:text-2xl">
                {prev.name}
              </span>
            </span>
          </Link>
          <Link
            to={`${SERVICES_PATH}/${next.slug}`}
            data-testid="service-next-link"
            className="group flex items-center gap-3 text-right"
          >
            <span>
              <span className="block font-mono-alt text-[9px] uppercase tracking-[0.25em] text-stone-600">Next</span>
              <span className="font-display text-lg font-light text-stone-300 transition-colors group-hover:text-amber-200 sm:text-2xl">
                {next.name}
              </span>
            </span>
            <ArrowRight className="h-4 w-4 text-stone-600 transition-colors group-hover:text-amber-300" />
          </Link>
        </div>
      </section>

      <CTASection
        copy={`Message us on WhatsApp and we will arrange your ${service.name} — private suite, warm oils, unhurried hands.`}
      />
    </>
  );
}
